import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { getAllStockBundles } from '@/lib/queries/bundles'
import type { StockBundle } from '@/lib/queries/bundles'

/**
 * Bundles List page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: bundles-list.html
 */
export default function BundlesList() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const { data: bundles, isLoading } = useQuery({
    queryKey: ['stockBundles'],
    queryFn: getAllStockBundles,
  })

  // Filter bundles based on search query
  const filteredBundles = useMemo(() => {
    if (!bundles) return []
    if (!searchQuery.trim()) return bundles

    const query = searchQuery.toLowerCase().trim()
    return bundles.filter((bundle) => {
      const titleMatch = bundle.title?.toLowerCase().includes(query)
      const skuMatch = bundle.sku?.toLowerCase().includes(query)
      return titleMatch || skuMatch
    })
  }, [bundles, searchQuery])

  // Helper to get status (Published/Draft) based on Zoho sync status
  const getStatus = (bundle: StockBundle): 'published' | 'draft' => {
    if (bundle.zohoMapping?.zohoSyncStatus === 'synced') {
      return 'published'
    }
    return 'draft'
  }

  // Helper to get Zoho status display text
  const getZohoStatusText = (bundle: StockBundle): string => {
    const status = bundle.zohoMapping?.zohoSyncStatus
    switch (status) {
      case 'synced':
        return 'Synced'
      case 'error':
        return 'Error'
      case 'not_synced':
      default:
        return 'Not Published'
    }
  }

  // Helper to get components count
  const getComponentsCount = (bundle: StockBundle): number => {
    return bundle.stockOrder?.stockBundleStockItems?.length || 0
  }

  // Helper to format price
  const formatPrice = (price: number): string => {
    return `£${price.toFixed(2)}`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading bundles...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Bundles
            {bundles && (
              <span className="text-lg font-normal text-gray-500 ml-2">
                ({filteredBundles.length})
              </span>
            )}
          </h1>
          <input
            type="search"
            placeholder="Search bundles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {filteredBundles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'No bundles found matching your search' : 'No bundles found'}
            </p>
            {!searchQuery && (
              <Link
                to="/bundles/new"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
              >
                Create Your First Bundle
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBundles.map((bundle) => {
              const status = getStatus(bundle)
              const componentsCount = getComponentsCount(bundle)
              const zohoStatus = getZohoStatusText(bundle)
              const price = bundle.selling?.sellAt ? formatPrice(bundle.selling.sellAt) : 'N/A'
              const stockLevel = bundle.stockOrder?.availableStock ?? 0
              const isPublished = status === 'published'

              return (
                <div
                  key={bundle._id}
                  onClick={() => navigate(`/bundles/${bundle._id}`)}
                  className="block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow touch-target-min-44 cursor-pointer"
                >
                  {/* Bundle Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                        {bundle.title || 'Untitled Bundle'}
                      </h3>
                      <p className="text-xs text-gray-600">SKU: {bundle.sku || 'No SKU'}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                        status === 'published'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {/* Bundle Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Components</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {componentsCount} {componentsCount === 1 ? 'item' : 'items'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Zoho Status</div>
                      <div className="text-sm font-semibold text-gray-900">{zohoStatus}</div>
                    </div>
                  </div>

                  {/* Bundle Cost */}
                  <div className="text-xl font-bold text-blue-600 mt-3">{price}</div>

                  {/* Stock Level (only for published bundles) */}
                  {isPublished && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs text-gray-600 mb-1">Stock Level</div>
                      <div
                        className={`text-lg font-bold ${
                          stockLevel === 0 ? 'text-red-600' : 'text-gray-900'
                        }`}
                      >
                        {stockLevel} {stockLevel === 1 ? 'unit' : 'units'}
                      </div>
                      {/*
                        Link to Inventory composite item detail in Zoho using mapped item id.
                        Fallback to /inventory when id is missing.
                      */}
                      <Link
                        to={
                          bundle.zohoMapping?.zohoItemId
                            ? `/inventory/${encodeURIComponent(bundle.zohoMapping.zohoItemId)}`
                            : '/inventory'
                        }
                        onClick={(e) => e.stopPropagation()}
                        className="block w-full mt-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-center hover:bg-gray-200 transition-colors touch-target-min-44"
                      >
                        View Stock Details
                      </Link>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Floating Action Button */}
      <Link
        to="/bundles/new"
        className="fixed bottom-24 right-5 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-light shadow-lg hover:bg-blue-700 transition-colors touch-target-min-44 z-50"
        aria-label="Create new bundle"
      >
        +
      </Link>
    </div>
  )
}
